import React from 'react';
import { CheckCircle, UserPlus, XCircle, Clock, Receipt, AlertTriangle, Timer } from 'lucide-react';
import { Decision, NiwtAgent } from '../types';
import { emitDecisionApprove, emitDecisionDelegate, emitDecisionSnooze, emitDecisionOpenReceipts } from '../events';

interface Props {
  decision: Decision;
  agents: NiwtAgent[];
  onReject: (decision: Decision) => void;
  onOpenReceipts: (decision: Decision) => void;
}

const needsTrustReview = (d: Decision) =>
  d.confidence < 0.6 || d.receipts.length === 0;

const DecisionCard: React.FC<Props> = ({ decision, agents, onReject, onOpenReceipts }) => {
  const lowTrust = needsTrustReview(decision);

  const delegateAgent = agents.find((a) => a.id === decision.recommended_delegate_agent_id);

  const handleApprove = () => {
    if (lowTrust) {
      const confirmed = window.confirm(
        'Low confidence or limited receipts — approve anyway?'
      );
      if (!confirmed) return;
    }
    emitDecisionApprove(decision.id, decision.recommended_delegate_agent_id);
  };

  const handleDelegate = () => {
    emitDecisionDelegate(decision.id, decision.recommended_delegate_agent_id);
  };

  const handleSnooze = () => {
    emitDecisionSnooze(decision.id);
  };

  const handleOpenReceipts = () => {
    emitDecisionOpenReceipts(decision.id);
    onOpenReceipts(decision);
  };

  const confidencePct = Math.round(decision.confidence * 100);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 relative">
      {/* Trust gate badge */}
      {lowTrust && (
        <div className="absolute top-3 right-3 flex items-center gap-1 bg-amber-50 text-amber-700 text-xs font-medium px-2 py-0.5 rounded-full border border-amber-200">
          <AlertTriangle className="w-3 h-3" />
          Needs review
        </div>
      )}

      {/* Header */}
      <div className="pr-24">
        <h3 className="text-sm font-semibold text-gray-900 leading-snug">{decision.title}</h3>
      </div>

      {/* Summary */}
      <p className="mt-2 text-sm text-gray-600 leading-relaxed">{decision.summary}</p>

      {/* Why now */}
      <p className="mt-1.5 text-xs text-amber-700 font-medium">
        ⏰ {decision.why_now}
      </p>

      {/* Meta row */}
      <div className="mt-3 flex flex-wrap items-center gap-3">
        {/* Impact */}
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <span className="font-medium text-gray-700">{decision.impact.label}:</span>
          <span className="font-semibold text-gray-900">{decision.impact.value}</span>
        </div>

        {/* Time estimate */}
        <div className="flex items-center gap-1 text-xs text-gray-400">
          <Timer className="w-3 h-3" />
          {decision.time_estimate_minutes} min
        </div>

        {/* Confidence */}
        <div className="flex items-center gap-1 bg-purple-50 text-niwt-primary text-xs rounded-full px-2 py-0.5">
          <span>{confidencePct}% confidence</span>
        </div>

        {/* Receipts count */}
        <button
          onClick={handleOpenReceipts}
          className="flex items-center gap-1 text-xs text-niwt-primary hover:text-niwt-secondary font-medium transition-colors"
        >
          <Receipt className="w-3 h-3" />
          {decision.receipts.length} receipt{decision.receipts.length !== 1 ? 's' : ''}
        </button>
      </div>

      {/* Recommended delegate */}
      {delegateAgent && (
        <p className="mt-2 text-xs text-gray-400">
          Recommended: delegate to{' '}
          <span className="font-medium text-gray-600">{delegateAgent.display_name}</span>
        </p>
      )}

      {/* Actions */}
      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-gray-50 pt-3">
        <button
          onClick={handleApprove}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 hover:bg-green-100 rounded-lg text-xs font-medium transition-colors border border-green-200"
        >
          <CheckCircle className="w-3.5 h-3.5" />
          Approve
        </button>

        <button
          onClick={handleDelegate}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 text-gray-600 hover:bg-gray-100 rounded-lg text-xs font-medium transition-colors border border-gray-200"
        >
          <UserPlus className="w-3.5 h-3.5" />
          Delegate
        </button>

        <button
          onClick={() => onReject(decision)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg text-xs font-medium transition-colors border border-red-200"
        >
          <XCircle className="w-3.5 h-3.5" />
          Reject
        </button>

        <button
          onClick={handleSnooze}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 text-gray-500 hover:bg-gray-100 rounded-lg text-xs font-medium transition-colors border border-gray-200"
        >
          <Clock className="w-3.5 h-3.5" />
          Snooze
        </button>
      </div>
    </div>
  );
};

export default DecisionCard;
